/**
 * OnboardingOverlay - Main composition component
 *
 * Renders as a Portal on document.body at z-[9999].
 * Composes SpotlightBackdrop + OnboardingTooltip.
 * Handles:
 * - Element targeting via data-tour selectors
 * - Position calculation with viewport awareness
 * - Resize and scroll observation
 * - Smooth transitions between steps
 * - Interactive steps: click listener on target element
 * - Phase 1 completion transitional message
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useOnboarding } from '@/contexts/OnboardingContext';
import { useTranslation } from '@/contexts/LanguageContext';
import SpotlightBackdrop from './SpotlightBackdrop';
import OnboardingTooltip from './OnboardingTooltip';
import { Button } from '@/components/ui/button';
import { PartyPopper } from 'lucide-react';
import {
  getElementByTourSelector,
  getElementRect,
  getSpotlightRect,
  isElementVisible,
  scrollToElement,
  waitForElement,
  calculateTooltipPosition,
  isMobileViewport,
  ElementRect,
  TooltipPosition,
} from '@/lib/onboardingUtils';
import {
  getPhaseChapters,
  getPhaseTotalSteps,
  getPhaseGlobalStepIndex,
  getPhaseChapterIndex,
} from '@/lib/onboardingTour';

const DEFAULT_TOOLTIP_SIZE = { width: 300, height: 220 };

const OnboardingOverlay: React.FC = () => {
  const {
    state,
    isOverlayVisible,
    currentChapter,
    currentStep,
    nextStep,
    prevStep,
    dismissTour,
  } = useOnboarding();
  const { t } = useTranslation();

  const [targetRect, setTargetRect] = useState<ElementRect | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<TooltipPosition>({
    top: 0,
    left: 0,
    arrowDirection: 'none',
  });
  const [tooltipSize, setTooltipSize] = useState(DEFAULT_TOOLTIP_SIZE);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showPhase1Complete, setShowPhase1Complete] = useState(false);

  const observerRef = useRef<ResizeObserver | null>(null);
  const rafRef = useRef<number | null>(null);
  const nextStepRef = useRef(nextStep);
  nextStepRef.current = nextStep;

  // ============================================================================
  // PHASE 1 COMPLETE TRANSITIONAL MESSAGE
  // ============================================================================

  useEffect(() => {
    const handler = () => setShowPhase1Complete(true);
    window.addEventListener('cai:tour-phase1-complete', handler);
    return () => window.removeEventListener('cai:tour-phase1-complete', handler);
  }, []);

  useEffect(() => {
    if (showPhase1Complete) {
      const timer = setTimeout(() => setShowPhase1Complete(false), 6000);
      return () => clearTimeout(timer);
    }
  }, [showPhase1Complete]);

  // ============================================================================
  // ELEMENT TARGETING & POSITIONING
  // ============================================================================

  const updatePosition = useCallback(() => {
    if (!currentStep || !isOverlayVisible) {
      setTargetRect(null);
      return;
    }

    // Center placement (no target)
    if (!currentStep.spotlight || currentStep.placement === 'center') {
      setTargetRect(null);
      const pos = calculateTooltipPosition(
        { x: 0, y: 0, width: 0, height: 0 },
        'center',
        tooltipSize.width,
        tooltipSize.height,
      );
      setTooltipPosition(pos);
      return;
    }

    const isMobile = isMobileViewport();
    const selector = isMobile && currentStep.mobileTargetSelector
      ? currentStep.mobileTargetSelector
      : currentStep.targetSelector;

    const el = getElementByTourSelector(selector);

    if (!el || !isElementVisible(el)) {
      // Element not found - show as centered modal fallback
      setTargetRect(null);
      const pos = calculateTooltipPosition(
        { x: 0, y: 0, width: 0, height: 0 },
        'center',
        tooltipSize.width,
        tooltipSize.height,
      );
      setTooltipPosition(pos);
      return;
    }

    const elRect = getElementRect(el);
    const spotlightRect = getSpotlightRect(elRect);
    setTargetRect(spotlightRect);

    const placement = isMobile && currentStep.mobilePlacement
      ? currentStep.mobilePlacement
      : currentStep.placement;

    const pos = calculateTooltipPosition(
      elRect,
      placement,
      tooltipSize.width,
      tooltipSize.height,
    );
    setTooltipPosition(pos);
  }, [currentStep, isOverlayVisible, tooltipSize]);

  // ============================================================================
  // STEP CHANGE EFFECT
  // ============================================================================

  useEffect(() => {
    if (!currentStep || !isOverlayVisible) return;

    let cancelled = false;
    setIsTransitioning(true);

    const targetStep = async () => {
      if (!currentStep.spotlight || currentStep.placement === 'center') {
        // No element to wait for
        if (!cancelled) {
          updatePosition();
          setIsTransitioning(false);
        }
        return;
      }

      const isMobile = isMobileViewport();
      const selector = isMobile && currentStep.mobileTargetSelector
        ? currentStep.mobileTargetSelector
        : currentStep.targetSelector;

      if (!selector) {
        if (!cancelled) {
          updatePosition();
          setIsTransitioning(false);
        }
        return;
      }

      // Wait for element to appear
      const el = await waitForElement(selector, 5000);
      if (cancelled) return;

      if (el) {
        await scrollToElement(el);
        if (cancelled) return;
      }

      updatePosition();
      setIsTransitioning(false);

      // Recalculate after layout stabilizes (children may render async)
      setTimeout(() => {
        if (!cancelled) updatePosition();
      }, 500);
    };

    // Small delay for page transition
    const timer = setTimeout(targetStep, 100);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [currentStep?.id, isOverlayVisible]);

  // ============================================================================
  // INTERACTIVE STEP: CLICK LISTENER ON TARGET ELEMENT
  // ============================================================================

  useEffect(() => {
    if (!currentStep || !isOverlayVisible || !currentStep.interactive) return;
    if (!currentStep.spotlight || currentStep.placement === 'center') return;

    const isMobile = isMobileViewport();
    const selector = isMobile && currentStep.mobileTargetSelector
      ? currentStep.mobileTargetSelector
      : currentStep.targetSelector;
    if (!selector) return;

    const el = getElementByTourSelector(selector);
    if (!el) return;

    const handleClick = (e: Event) => {
      // Let the element's native click action proceed, then advance tour
      setTimeout(() => {
        nextStepRef.current();
      }, 300);
    };

    el.addEventListener('click', handleClick, { capture: true, once: true });

    return () => {
      el.removeEventListener('click', handleClick, true);
    };
  }, [currentStep?.id, isOverlayVisible, currentStep?.interactive]);

  // ============================================================================
  // RESIZE & SCROLL OBSERVATION
  // ============================================================================

  useEffect(() => {
    if (!isOverlayVisible) return;

    const handleResize = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(updatePosition);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize, true);

    // ResizeObserver on the target element
    observerRef.current = new ResizeObserver(handleResize);
    const isMobile = isMobileViewport();
    const selector = currentStep && isMobile && currentStep.mobileTargetSelector
      ? currentStep.mobileTargetSelector
      : currentStep?.targetSelector;

    if (selector) {
      const el = getElementByTourSelector(selector);
      if (el) observerRef.current.observe(el);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize, true);
      if (observerRef.current) observerRef.current.disconnect();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [isOverlayVisible, currentStep?.id, updatePosition]);

  // ============================================================================
  // TOOLTIP MEASURE CALLBACK
  // ============================================================================

  const handleTooltipMeasure = useCallback((width: number, height: number) => {
    setTooltipSize({ width, height });
  }, []);

  // Recalculate when tooltip size changes
  useEffect(() => {
    if (isOverlayVisible) updatePosition();
  }, [tooltipSize, updatePosition, isOverlayVisible]);

  // ============================================================================
  // RENDER: Phase 1 Complete Transitional Message
  // ============================================================================

  if (showPhase1Complete) {
    return createPortal(
      <div className="onboarding-overlay" style={{ position: 'fixed', inset: 0, zIndex: 9999 }}>
        <SpotlightBackdrop
          targetRect={null}
          showSpotlight={false}
          onClick={() => setShowPhase1Complete(false)}
        />
        <div
          className="bg-popover text-popover-foreground rounded-xl shadow-lg border p-6 space-y-4 text-center"
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            maxWidth: 360,
            width: 'calc(100% - 32px)',
            zIndex: 10000,
          }}
        >
          <PartyPopper className="h-10 w-10 mx-auto text-primary" />
          <h3 className="text-lg font-semibold">
            {t('onboarding.steps.phase1Complete.title')}
          </h3>
          <p className="text-sm text-muted-foreground">
            {t('onboarding.steps.phase1Complete.description')}
          </p>
          <Button size="sm" onClick={() => setShowPhase1Complete(false)}>
            {t('onboarding.controls.gotIt')}
          </Button>
        </div>
      </div>,
      document.body,
    );
  }

  // ============================================================================
  // RENDER: Tour Overlay
  // ============================================================================

  if (!isOverlayVisible || !state.isActive || !currentStep || !currentChapter) {
    return null;
  }

  // Phase-aware progress calculations
  const phase = state.phase || 'phase1';
  const chapterIndex = getPhaseChapterIndex(phase, currentChapter.id);
  const globalStepIndex = getPhaseGlobalStepIndex(phase, currentChapter.id, state.currentStepIndex);
  const totalSteps = getPhaseTotalSteps(phase);
  const phaseChapters = getPhaseChapters(phase);
  const totalChapters = phaseChapters.length;

  // Check if this is the very first or last step of the current phase
  const isFirstStep = chapterIndex === 0 && state.currentStepIndex === 0;
  const lastPhaseChapter = phaseChapters[phaseChapters.length - 1];
  const isLastStep =
    currentChapter.id === lastPhaseChapter.id &&
    state.currentStepIndex === lastPhaseChapter.steps.length - 1;

  const overlay = (
    <div
      className={`onboarding-overlay ${isTransitioning ? 'onboarding-transitioning' : ''}`}
      aria-live="polite"
    >
      <SpotlightBackdrop
        targetRect={targetRect}
        showSpotlight={currentStep.spotlight}
        interactive={currentStep.interactive}
        onClick={dismissTour}
      />

      <OnboardingTooltip
        titleKey={currentStep.titleKey}
        descriptionKey={currentStep.descriptionKey}
        position={tooltipPosition}
        chapterTitleKey={currentChapter.titleKey}
        chapterIndex={chapterIndex}
        totalChapters={totalChapters}
        globalStepIndex={globalStepIndex}
        totalSteps={totalSteps}
        isFirstStep={isFirstStep}
        isLastStep={isLastStep}
        interactive={currentStep.interactive}
        onMeasure={handleTooltipMeasure}
        onNext={nextStep}
        onPrev={prevStep}
        onDismiss={dismissTour}
      />
    </div>
  );

  return createPortal(overlay, document.body);
};

export default OnboardingOverlay;
